import { useState } from 'react';
import { resolveImagePath } from '@/utils/imageResolver';

interface NavigationItem {
  label: string;
  href: string;
  children?: NavigationItem[];
}

const topNavItems: NavigationItem[] = [
  { label: 'Recent News', href: '/category/news/' },
  { label: 'Helpful Links', href: '/helpful-links/' },
  { label: 'About Us', href: '/about-us/' },
  { label: 'Contact Us', href: '/contact-us/' },
];

const mainNavItems: NavigationItem[] = [
  { label: 'Home', href: '/' },
  { label: 'Courses', href: '/courses/' },
  { 
    label: 'Intro Courses', 
    href: '#',
    children: [
      { label: 'Free Ground School Series', href: '/aerodynamics/' },
      { label: 'Free Flight School Series', href: '/introduction/' },
    ]
  },
  { label: 'Our Fleet', href: '/our-fleet/' },
];

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isIntroCoursesOpen, setIsIntroCoursesOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header id="site-header" className="bg-white shadow-sm">
      {/* Top Bar */}
      <div className="bg-secondary text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Social Media Links */}
            <div className="flex items-center space-x-4">
              <a 
                href="https://www.facebook.com/americanaviationflightacademy/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-red-200 transition-colors"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/americanaviationflightacademy/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-red-200 transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.291C3.897 14.307 3.365 12.958 3.365 11.4c0-1.558.532-2.907 1.761-4.297.875-.801 2.026-1.291 3.323-1.291 1.297 0 2.448.49 3.323 1.291 1.229 1.39 1.761 2.739 1.761 4.297 0 1.558-.532 2.907-1.761 4.297-.875.801-2.026 1.291-3.323 1.291zm7.718-1.134c-.385-.298-.711-.848-.711-1.611V8.987c0-.763.326-1.313.711-1.611.385-.298.889-.447 1.423-.447.534 0 1.038.149 1.423.447.385.298.711.848.711 1.611v5.256c0 .763-.326 1.313-.711 1.611-.385.298-.889.447-1.423.447-.534 0-1.038-.149-1.423-.447z"/>
                </svg>
              </a>
            </div>

            {/* Top Navigation Links */}
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-6">
                {topNavItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-sm font-medium text-white hover:text-red-200 transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
              
              {/* Start Now Button */}
              <a
                href="/contact-us/"
                className="bg-white text-secondary px-4 py-1 text-sm font-condensed font-medium rounded hover:bg-red-50 transition-colors"
              >
                Start Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" className="flex items-center">
                <img
                  src={resolveImagePath('/images/logo.png')}
                  alt="American Aviation – Flight Academy"
                  className="h-12 w-auto"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8">
              {mainNavItems.map((item) => (
                <div key={item.label} className="relative">
                  {item.children ? (
                    <div className="relative">
                      <button
                        className="flex items-center text-primary-blue font-condensed font-medium hover:text-primary transition-colors"
                        onClick={() => setIsIntroCoursesOpen(!isIntroCoursesOpen)}
                      >
                        {item.label}
                        <svg className="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {isIntroCoursesOpen && (
                        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                          {item.children.map((child) => (
                            <a
                              key={child.label}
                              href={child.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              {child.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      className="text-primary-blue font-condensed font-medium hover:text-primary transition-colors"
                    >
                      {item.label}
                    </a>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                onClick={toggleMobileMenu}
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
              <nav className="space-y-4">
                {/* Main Navigation Items */}
                {mainNavItems.map((item) => (
                  <div key={item.label}>
                    {item.children ? (
                      <div>
                        <button
                          className="flex items-center w-full text-blue-900 font-medium py-2"
                          onClick={() => setIsIntroCoursesOpen(!isIntroCoursesOpen)}
                        >
                          {item.label}
                          <svg className="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {isIntroCoursesOpen && (
                          <div className="ml-4 space-y-2">
                            {item.children.map((child) => (
                              <a
                                key={child.label}
                                href={child.href}
                                className="block text-sm text-gray-600 py-1"
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <a
                        href={item.href}
                        className="block text-blue-900 font-medium py-2"
                      >
                        {item.label}
                      </a>
                    )}
                  </div>
                ))}

                {/* Top Navigation Items in Mobile */}
                <div className="pt-4 border-t border-gray-200">
                  {topNavItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="block text-gray-600 py-2"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </nav>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}